import { create } from 'zustand';
import api, { startTokenRefresh, stopTokenRefresh } from '../services/api';

// Helper to get initial auth state from storage
const getInitialAuthState = () => {
  try {
    // A superadmin tenant workspace runs in a separate window and stores its
    // scoped credentials in sessionStorage. Prefer those window-local values
    // so the original platform window keeps its superadmin session.
    const token = sessionStorage.getItem('accessToken') || localStorage.getItem('accessToken');
    const userStr = sessionStorage.getItem('user') || localStorage.getItem('user');
    
    // Clean up any stale 'token' key (legacy)
    if (localStorage.getItem('token')) {
      localStorage.removeItem('token');
    }
    
    if (token && userStr) {
      const user = JSON.parse(userStr);
      const memberships = user?.memberships || [];
      const requiresTenantSelection = Boolean(
        !user?.is_superuser &&
        user?.role !== 'superadmin' &&
        !user?.tenant_id &&
        Array.isArray(memberships) &&
        memberships.length > 1
      );
      return {
        user,
        token,
        isAuthenticated: true,
        isInitialized: true,  // Auth state is ready
        requiresTenantSelection,
        memberships,
      };
    }
  } catch (error) {
    console.error('Failed to parse user data from storage:', error);
  }
  return {
    user: null,
    token: null,
    isAuthenticated: false,
    isInitialized: true,  // Auth state is ready (just not logged in)
    requiresTenantSelection: false,
    memberships: [],
  };
};

// Initialize state from localStorage immediately
const initialState = getInitialAuthState();

export const useAuthStore = create((set, get) => ({
  // Initialize with persisted state
  ...initialState,

  login: async (username, password) => {
    const response = await api.post('/auth/login', { username, password });
    return get()._applyLoginResponse(response.data);
  },

  // turnstileToken: the Cloudflare Turnstile token from the form, when enabled.
  requestEmailCode: async (email, turnstileToken) => {
    const response = await api.post('/auth/email/request-code', {
      email, ...(turnstileToken ? { turnstile_token: turnstileToken } : {}),
    });
    return response.data;
  },

  requestSignupCode: async (email, turnstileToken) => {
    const response = await api.post('/auth/signup/request-code', {
      email, ...(turnstileToken ? { turnstile_token: turnstileToken } : {}),
    });
    return response.data;
  },

  completeSignupWithCode: async (payload) => {
    const response = await api.post('/auth/signup/verify-code', payload);
    return get()._applyLoginResponse(response.data);
  },

  loginWithEmailCode: async (email, code) => {
    const response = await api.post('/auth/email/verify-code', { email, code });
    // Superadmins don't get a session from the code alone — the backend
    // returns a step_up_token instead, and the caller must collect the
    // account password and call verifySuperadminPassword to finish.
    if (response.data?.requires_password) {
      return response.data;
    }
    return get()._applyLoginResponse(response.data);
  },

  verifySuperadminPassword: async (email, stepUpToken, password) => {
    const response = await api.post('/auth/email/verify-superadmin-password', {
      email,
      step_up_token: stepUpToken,
      password,
    });
    return get()._applyLoginResponse(response.data);
  },

  // Shared by every login path — same token/state handling regardless of how
  // the user authenticated, so a new path can't silently drift from what the
  // rest of the app expects in localStorage/store state.
  _applyLoginResponse: (data) => {
    const { access_token, user, requires_tenant_selection, memberships } = data;

    localStorage.removeItem('token');
    sessionStorage.removeItem('accessToken');
    sessionStorage.removeItem('user');
    sessionStorage.removeItem('tenantAdminMode');
    localStorage.setItem('accessToken', access_token);
    localStorage.setItem('user', JSON.stringify(user));

    set({
      user: user,
      token: access_token,
      isAuthenticated: true,
      isInitialized: true,
      requiresTenantSelection: !!requires_tenant_selection,
      memberships: memberships || user?.memberships || [],
    });

    // Only start refresh if we have an active tenant. Picker-state tokens
    // can't be refreshed meaningfully — they're a transient stop on the
    // way to /auth/select-tenant.
    if (!requires_tenant_selection) {
      startTokenRefresh();
    }

    return data;
  },

  switchTenant: async (tenantId, { sessionOnly = false } = {}) => {
    const response = await api.post('/auth/select-tenant', { tenant_id: tenantId });
    const { access_token, user } = response.data;

    const storage = sessionOnly ? sessionStorage : localStorage;
    storage.setItem('accessToken', access_token);
    storage.setItem('user', JSON.stringify(user));
    storage.setItem('lastTenantId', tenantId);
    if (sessionOnly) {
      sessionStorage.setItem('tenantAdminMode', 'true');
    } else {
      sessionStorage.removeItem('accessToken');
      sessionStorage.removeItem('user');
      sessionStorage.removeItem('lastTenantId');
      sessionStorage.removeItem('tenantAdminMode');
    }

    set({
      user: user,
      token: access_token,
      isAuthenticated: true,
      isInitialized: true,
      requiresTenantSelection: false,
      memberships: user?.memberships || [],
    });

    // Restart refresh on the new active tenant.
    stopTokenRefresh();
    startTokenRefresh();

    return response.data;
  },

  updateUserProfile: (patch) => {
    const current = get().user;
    if (!current) return null;
    const updated = { ...current, ...patch };
    const storage = sessionStorage.getItem('accessToken') ? sessionStorage : localStorage;
    storage.setItem('user', JSON.stringify(updated));
    set({ user: updated });
    return updated;
  },

  logout: () => {
    // Stop token refresh interval
    stopTokenRefresh();

    const tenantAdminTab = sessionStorage.getItem('tenantAdminMode') === 'true';
    if (!tenantAdminTab) {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('user');
      localStorage.removeItem('token');  // Clean up legacy key
    }
    sessionStorage.removeItem('accessToken');
    sessionStorage.removeItem('user');
    sessionStorage.removeItem('lastTenantId');
    sessionStorage.removeItem('tenantAdminMode');

    set({
      user: null,
      token: null,
      isAuthenticated: false,
      isInitialized: true,
      requiresTenantSelection: false,
      memberships: [],
    });
  },

  initializeAuth: () => {
    const token = sessionStorage.getItem('accessToken') || localStorage.getItem('accessToken');
    const userStr = sessionStorage.getItem('user') || localStorage.getItem('user');
    
    // Clean up any stale 'token' key
    if (localStorage.getItem('token')) {
      localStorage.removeItem('token');
    }
    
    if (token && userStr) {
      try {
        const user = JSON.parse(userStr);
        const memberships = user?.memberships || [];
        const requiresTenantSelection = Boolean(
          !user?.is_superuser &&
          user?.role !== 'superadmin' &&
          !user?.tenant_id &&
          Array.isArray(memberships) &&
          memberships.length > 1
        );
        set({
          user,
          token,
          isAuthenticated: true,
          isInitialized: true,
          requiresTenantSelection,
          memberships,
        });
        if (!requiresTenantSelection) {
          startTokenRefresh();
        }
      } catch (error) {
        console.error('Failed to parse user data from storage:', error);
        get().logout();
      }
    } else {
      set({ isInitialized: true });
    }
  },
}));

export default useAuthStore;
